import styled from '@emotion/styled';
import { Radio as $Radio } from 'antd';
import { RadioChangeEvent, RadioGroupProps as $RadioGroupProps, RadioProps as $RadioProps } from 'antd/lib/radio';
import { CheckboxOptionType } from 'antd/lib/checkbox';
import React, { useRef } from 'react';

import { Input, OnBlurCallback, OnChangeCallback, FormElementChangeEvent } from '.';

const OtherInput = styled(Input)`
  &.ant-input {
    width: auto;
    height: 32px;
    margin-left: 5px;
    padding-top: 0;
    padding-bottom: 0;
  }
`;

const getOptionValue = (option: CheckboxOptionType | string | number) => {
  return (option as CheckboxOptionType)?.value ?? option;
};
const getOptionLabel = (option: CheckboxOptionType | string | number) => {
  return (option as CheckboxOptionType)?.label ?? option;
};

export type RadioProps = $RadioProps & {
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
};
export type RadioGroupProps = Omit<$RadioGroupProps, 'onChange'> & {
  hasOtherOption?: boolean;
  onBlur?: OnBlurCallback;
  onChange?: OnChangeCallback;
  isDeselectable?: boolean;
};

const Radio = function Radio(props: RadioProps) {
  return <$Radio {...props} />;
};

export const RadioGroup = React.memo(function RadioGroup({
  name,
  value: propValue,
  defaultValue,
  options,
  hasOtherOption = false,
  onBlur,
  onChange,
  isDeselectable,
  ...rest
}: RadioGroupProps) {
  // use `defaultValue` when the `value` does not exists but the `defaultValue` does
  const value = propValue === undefined && defaultValue !== undefined ? defaultValue : propValue;
  const otherOptionValue = useRef('');
  const isFocused = useRef(false);
  const handleFocus =
    onBlur &&
    (() => {
      isFocused.current = true;
    });
  const handleBlur =
    onBlur &&
    ((event: React.FocusEvent<HTMLInputElement>) => {
      const {
        target: { value },
      } = event;
      // check if the current radio button lost the focus because another one in the same group gained the focus,
      // then we consider the radiogroup itself still has focus
      setTimeout(() => {
        if (!isFocused.current && value) {
          onBlur({ name, value });
        }
      }, 16);
      isFocused.current = false;
    });
  const handleChange =
    onChange &&
    ((event: RadioChangeEvent) => {
      const {
        target: { value },
      } = event;
      onChange({ name, value: value }, !value ? { shouldValidate: false } : undefined);
    });
  const handleChangeOther =
    onChange &&
    (({ value = '' }: FormElementChangeEvent) => {
      onChange({ name, value });
    });
  const handleBlurOther =
    onChange &&
    (({ value }: FormElementChangeEvent) => {
      const cleanValue = typeof value === 'string' ? value.trim() : value;
      if (cleanValue !== value) {
        onChange({ name, value: cleanValue });
      }
      if (onBlur) {
        onBlur({ name, value: cleanValue });
      }
    });

  /**
   * Select the other option when
   * 1. the value is defined
   * 2. options has at least one item (options can be populated asynchronously)
   * 3. it's not one of the options in the options list
   */
  const isOtherOptionChecked =
    hasOtherOption &&
    value !== undefined &&
    !!options &&
    options.length > 0 &&
    !options.map((option) => getOptionValue(option)).includes(value);

  const getSelectedButtonClickHandler = (value: any) => () => {
    if (isDeselectable && onChange && value === propValue) {
      if (isOtherOptionChecked) {
        otherOptionValue.current = '';
      }
      onChange({ name, value: undefined }, { shouldValidate: false });
    }
  };

  if (isOtherOptionChecked) {
    otherOptionValue.current = value;
  }

  return (
    <$Radio.Group {...rest} name={name} value={value} onChange={handleChange}>
      {options &&
        options.map((option, index) => {
          const optionLabel = getOptionLabel(option);
          const optionValue = getOptionValue(option);

          return (
            <Radio
              key={`${index}-${optionValue}`}
              value={optionValue}
              onBlur={handleBlur}
              onFocus={handleFocus}
              onClick={getSelectedButtonClickHandler(optionValue)}
              disabled={(option as CheckboxOptionType)?.disabled ?? undefined}
            >
              {optionLabel}
            </Radio>
          );
        })}
      {hasOtherOption && (
        <Radio
          checked={isOtherOptionChecked}
          value={otherOptionValue.current}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onClick={getSelectedButtonClickHandler(otherOptionValue.current)}
        >
          Other...
          {isOtherOptionChecked && (
            <OtherInput
              value={otherOptionValue.current}
              onChange={handleChangeOther}
              onBlur={handleBlurOther}
              maxLength={250}
            />
          )}
        </Radio>
      )}
    </$Radio.Group>
  );
});
export default Radio;
